import * as ko from 'knockout';
import fetch from 'node-fetch';

import { AuthMethod, AuthState, MethodsResponse } from '../contracts';
import template from './MethodList.html';
import { ViewId, isAllowedUrl } from '../configuration';

class MethodList {

  title: ko.Observable<string> = ko.observable<string>("");
  subTitle: ko.Observable<string> = ko.observable<string>("");
  link1Text: ko.Observable<string> = ko.observable<string>("");
  link1Uri: ko.Observable<string> = ko.observable<string>("");
  link2Text: ko.Observable<string> = ko.observable<string>("");
  link2Uri: ko.Observable<string> = ko.observable<string>("");
  link3Text: ko.Observable<string> = ko.observable<string>("");
  link3Uri: ko.Observable<string> = ko.observable<string>("");
  link4Text: ko.Observable<string> = ko.observable<string>("");
  link4Uri: ko.Observable<string> = ko.observable<string>("");
  link5Text: ko.Observable<string> = ko.observable<string>("");
  link5Uri: ko.Observable<string> = ko.observable<string>("");
  loginMethods: ko.ObservableArray<AuthMethod> = ko.observableArray<AuthMethod>([]);

  // Computed properties - initialized in constructor
  selectedLanguage: ko.PureComputed<string>;
  activateView: ko.PureComputed<boolean>;

  constructor() {
    global.Logonme.log("MethodList:constructor");

    // Initialize computed properties
    this.selectedLanguage = ko.pureComputed<string>(() => global.Logonme.SelectedLanguage());
    this.activateView = ko.pureComputed<boolean>(() => global.Logonme.ActiveView() === ViewId.Methods);

    this.updateStrings(this.selectedLanguage());
    this.selectedLanguage.subscribe((lang: string) => {
      this.updateStrings(lang);
      this.doGetMethods();
    });

    this.activateView.subscribe((activate: boolean) => {
      if (activate) {
        global.Logonme.log("MethodList:activate");
        this.doGetMethods();
      }
    });

    global.Logonme.ViewConstructedCount(global.Logonme.ViewConstructedCount() + 1);
  }

  private updateStrings(lang: string): void {
    this.title(global.Logonme.LocalizationManager.getText("methods-list-title", lang));
    this.subTitle(global.Logonme.LocalizationManager.getText("methods-list-title-message", lang));

    this.link1Uri(global.Logonme.LocalizationManager.getText('methods-list-link1-src', lang, ''));
    this.link1Text(global.Logonme.LocalizationManager.getText('methods-list-link1-text', lang, ''));
    this.link2Uri(global.Logonme.LocalizationManager.getText('methods-list-link2-src', lang, ''));
    this.link2Text(global.Logonme.LocalizationManager.getText('methods-list-link2-text', lang, ''));
    this.link3Uri(global.Logonme.LocalizationManager.getText('methods-list-link3-src', lang, ''));
    this.link3Text(global.Logonme.LocalizationManager.getText('methods-list-link3-text', lang, ''));
    // footer
    this.link4Uri(global.Logonme.LocalizationManager.getText('methods-list-link4-src', lang, ''));
    this.link4Text(global.Logonme.LocalizationManager.getText('methods-list-link4-text', lang, ''));
    this.link5Uri(global.Logonme.LocalizationManager.getText('methods-list-link5-src', lang, ''));
    this.link5Text(global.Logonme.LocalizationManager.getText('methods-list-link5-text', lang, ''));
  }

  async doGetMethods(): Promise<void> {
    global.Logonme.log("doGetMethods");
    let response = await this.getMethods();

    if (response.authState === AuthState.PathFailed) {
      response = await this.getMethods();
      global.Logonme.AuthState(response.authState);
    }

    // only one method
    const sorted = response.challengeMethods?.sort((a, b) => (a.sortOrder < b.sortOrder) ? -1 : 1);
    if (sorted?.length === 1) {
      global.Logonme.SelectedMethod(sorted[0]);
      return;
    }

    // startup
    const method = sorted?.find((m: AuthMethod) => m.id === global.Logonme.Configuration.startupMethod);
    if (method) {
      global.Logonme.Configuration.startupMethod = null;
      global.Logonme.SelectedMethod(method);
    }

    this.loginMethods(sorted);
    global.Logonme.AuthState(response.authState);

    if (response.launchExternal && response.launchUri) {
      if (response.authState !== AuthState.PathCompletePendingFederation) {
        global.Logonme.LastLoginStore.remove();
      }

      const redirectUri = response.launchUri;
      const allowedTargets = global.Logonme.FrontendConfig.allowedRedirectTargets;
      if (!isAllowedUrl(redirectUri, allowedTargets)) {
        const msg = `Redirect blocked: '${redirectUri}' is not in the allowed redirect targets. Update 'Allowed Redirect Targets' in Authentication Service Settings.`;
        console.error(msg);
        alert(msg);
        return;
      }
      global.Logonme.log(`methods: re-directing to uri ${redirectUri}`);
      window.location.href = redirectUri;
    }
  }

  async doSelectMethod(method: AuthMethod): Promise<void> {
    this.loginMethods([]);
    global.Logonme.SelectedMethod(method);
  }

  private async getMethods(): Promise<MethodsResponse> {
    const uri = `${global.Logonme.Configuration.baseUrl}/ls/${global.Logonme.Configuration.tenant}/forms/methods`;
    const response = await fetch(uri);
    const methodsResponse = await response.json<MethodsResponse>();
    return methodsResponse;
  }
}

export default { viewModel: MethodList, template: template };
